import React, {useEffect, useState} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {isEmpty} from "@pg-mono/nodash";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {NAV_MOBILE_BREAKPOINT_WITH_FAVOURITES} from "./constants/nav_mobile_breakpoint";
import {IMobileBreakpoint, INavLeftElement, NavGtmData, NavLevelType} from "./Navigation";
import {INavElementStyle, NavigationElementText} from "./NavigationElement";

interface IProps {
    dropdownElements: INavLeftElement[];
    navState: number[];
    setNavState: (update: (prev: number[]) => number[]) => void;
    title: string;
    url?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    itemIndex: number;
    arrow: React.JSX.Element;
    dropdownIndicator?: React.JSX.Element;
    setBlendVisible: (isVisible: boolean) => void;
    icon?: React.JSX.Element;
    isRightElement?: boolean;
    mobileBreakpoint?: number;
    hidingBreakpoint?: number;
    style?: INavElementStyle;
    elementTracking: (trackData?: NavGtmData) => void;
    amplitudeNavClickTracking?: (selectedTab: string, sectionNumber: number) => void;
}

export const DropdownListElement = (props: IProps) => {
    const [isMobile, setMobile] = useState(false);

    useEffect(() => {
        setMobile(window.innerWidth < (props.mobileBreakpoint ?? 1024));
    }, [props.mobileBreakpoint]);

    const onClick = (
        e: React.MouseEvent<HTMLElement>,
        level: NavLevelType,
        idx: number,
        subElementsLength: number,
        onClick: ((e: React.MouseEvent<HTMLElement>) => void) | undefined,
        selectedTab: string,
        sectionNumber: number
    ) => {
        props.elementTracking({level: level.toString(), text: e.currentTarget.innerText});
        props.amplitudeNavClickTracking?.(selectedTab, sectionNumber);
        if (isMobile && subElementsLength > 0) {
            if (props.navState.length < 3) {
                e.preventDefault();
            }
            props.setNavState((prev) => [...prev.slice(0, level - 1), idx]);
        }
        if (onClick) {
            onClick(e); // if you need to prevent default you should do it in callback function
            props.setBlendVisible(false);
        }
    };

    const mobileShowLevel = (level: NavLevelType, idx: number) => {
        if (isMobile && props.navState[level] === idx) {
            return {display: "block"};
        }
    };

    const onHoverToggle = (isActive: boolean) => {
        if (!isMobile) {
            props.setBlendVisible(isActive);
        }
    };

    return (
        <TopLevelListElement
            mobileBreakpoint={props.mobileBreakpoint}
            data-testid={NAVIGATION_TEST_IDS.MAIN_ELEMENT}
            onMouseEnter={() => onHoverToggle(true)}
            onMouseLeave={() => onHoverToggle(false)}
            onClick={(e) => {
                if (isMobile) {
                    props.onClick?.(e);
                }
            }}
        >
            <TopLevelListElementContent
                mobileBreakpoint={props.mobileBreakpoint}
                onClick={(e) => onClick(e, 1, props.itemIndex, props.dropdownElements.length, props.onClick, props.title, props.itemIndex)}
                href={props.url}
            >
                <div css={iconAndTitleHolder}>
                    {props.icon && <div css={iconHolder}>{props.icon}</div>}
                    <NavigationElementText elementStyle={props.style}>{props.title}</NavigationElementText>
                </div>

                <TopLevelArrow mobileBreakpoint={props.mobileBreakpoint}>{!isEmpty(props.dropdownElements) && props.arrow}</TopLevelArrow>

                <DesktopOnlyIndicator mobileBreakpoint={props.mobileBreakpoint}>
                    {!isEmpty(props.dropdownElements) && props.dropdownIndicator}
                </DesktopOnlyIndicator>
            </TopLevelListElementContent>

            <FirstLevelHolder
                data-testid="navigation-first-level"
                isRightElement={props.isRightElement}
                mobileBreakpoint={props.mobileBreakpoint}
                className="first-level"
                style={mobileShowLevel(0, props.itemIndex)}
            >
                {props.dropdownElements.map((firstLevelItem, firstLevelItemIndex) => (
                    <FirstLevelListElement key={firstLevelItemIndex} mobileBreakpoint={props.mobileBreakpoint} data-testid="navigation-first-level-element">
                        <SubElement
                            mobileBreakpoint={props.mobileBreakpoint}
                            onClick={(e) =>
                                onClick(
                                    e,
                                    2,
                                    firstLevelItemIndex,
                                    firstLevelItem.subElements ? firstLevelItem.subElements.length : 0,
                                    firstLevelItem.onClick,
                                    `${props.title}|${firstLevelItem.title}`,
                                    firstLevelItemIndex
                                )
                            }
                            level={1}
                            href={firstLevelItem.url}
                            style={firstLevelItem.style}
                            target={firstLevelItem.target}
                        >
                            {firstLevelItem.title}

                            <div>{!isEmpty(firstLevelItem.subElements) && props.arrow}</div>
                        </SubElement>

                        {!isEmpty(firstLevelItem.subElements) && (
                            <SubLevelHolder
                                mobileBreakpoint={props.mobileBreakpoint}
                                data-testid="navigation-second-level"
                                className="second-level"
                                style={mobileShowLevel(1, firstLevelItemIndex)}
                            >
                                {firstLevelItem.subElements?.map((secondaryLevelItem, secondaryLevelItemIndex) => (
                                    <SecondLevelListElement
                                        key={secondaryLevelItemIndex}
                                        mobileBreakpoint={props.mobileBreakpoint}
                                        data-testid="navigation-second-level-element"
                                        hideOnDesktop={secondaryLevelItem.hideOnDesktop}
                                    >
                                        <SubElement
                                            mobileBreakpoint={props.mobileBreakpoint}
                                            onClick={(e) =>
                                                onClick(
                                                    e,
                                                    3,
                                                    secondaryLevelItemIndex,
                                                    secondaryLevelItem.subElements ? secondaryLevelItem.subElements.length : 0,
                                                    secondaryLevelItem.onClick,
                                                    `${props.title}|${firstLevelItem.title}|${secondaryLevelItem.title}`,
                                                    secondaryLevelItemIndex
                                                )
                                            }
                                            level={2}
                                            href={secondaryLevelItem.url}
                                            target={secondaryLevelItem.target}
                                        >
                                            {secondaryLevelItem.title}

                                            <div>{!isEmpty(secondaryLevelItem.subElements) && props.arrow}</div>
                                        </SubElement>

                                        {!isEmpty(secondaryLevelItem.subElements) && (
                                            <SubLevelHolder
                                                mobileBreakpoint={props.mobileBreakpoint}
                                                data-testid="navigation-third-level"
                                                className="third-level"
                                                style={mobileShowLevel(2, secondaryLevelItemIndex)}
                                            >
                                                {secondaryLevelItem.subElements?.map((thirdLevelListElement, thirdLevelItemIndex) => {
                                                    return (
                                                        <li key={thirdLevelItemIndex} data-testid="navigation-third-level-element">
                                                            <SubElement
                                                                mobileBreakpoint={props.mobileBreakpoint}
                                                                onClick={(e) =>
                                                                    onClick(
                                                                        e,
                                                                        4,
                                                                        thirdLevelItemIndex,
                                                                        thirdLevelListElement.subElements ? thirdLevelListElement.subElements.length : 0,
                                                                        thirdLevelListElement.onClick,
                                                                        `${props.title}|${firstLevelItem.title}|${secondaryLevelItem.title}|${thirdLevelListElement.title}`,
                                                                        thirdLevelItemIndex
                                                                    )
                                                                }
                                                                level={3}
                                                                href={thirdLevelListElement.url}
                                                                target={thirdLevelListElement.target}
                                                            >
                                                                {thirdLevelListElement.title}
                                                            </SubElement>
                                                        </li>
                                                    );
                                                })}
                                            </SubLevelHolder>
                                        )}
                                    </SecondLevelListElement>
                                ))}
                            </SubLevelHolder>
                        )}
                    </FirstLevelListElement>
                ))}
            </FirstLevelHolder>
        </TopLevelListElement>
    );
};

export interface IFirstLevelHolderProps extends IMobileBreakpoint {
    isRightElement?: boolean;
}

interface ISecondLevelElementProps extends IMobileBreakpoint {
    hideOnDesktop?: boolean;
}

// TOP LEVEL
const TopLevelListElement = styled.li<IMobileBreakpoint>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        display: inline-block;
        width: unset;
        position: relative;

        &:hover {
            .first-level {
                display: block;
            }

            .dropdown-indicator svg {
                transform: rotate(180deg);
            }
        }
    }
`;

const TopLevelListElementContent = styled.a<IMobileBreakpoint>`
    height: 60px;
    padding: 2rem 2.5rem 2rem 4rem;
    font-size: 1.24rem;
    color: #23232d;
    cursor: pointer;
    width: 320px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover,
    &:focus {
        text-decoration: none;
        color: #23232d;
    }

    @media (min-width: 320px) {
        width: 320px;
    }

    @media (min-width: 360px) {
        width: 360px;
    }

    @media (min-width: 375px) {
        width: 375px;
    }

    @media (min-width: 414px) {
        width: 414px;
    }

    @media (min-width: 500px) {
        width: 360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        width: unset;
        padding: 2rem;
        white-space: nowrap;
    }

    ${(props) =>
        props.mobileBreakpoint && props.mobileBreakpoint >= NAV_MOBILE_BREAKPOINT_WITH_FAVOURITES
            ? ""
            : `
        @media (min-width: ${NAV_MOBILE_BREAKPOINT_WITH_FAVOURITES}px) {
            padding: 2rem 3rem;
        }
    `}
`;

const TopLevelArrow = styled.div<IMobileBreakpoint>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        display: none;
    }
`;

const DesktopOnlyIndicator = styled.span<IMobileBreakpoint>`
    display: none;

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        display: inline-flex;
    }
`;

// FIRST LEVEL
const FirstLevelHolder = styled.ul<IFirstLevelHolderProps>`
    display: none;
    position: absolute;
    background: #fff;
    list-style: none;
    padding: 0;
    margin: 0;
    top: 0;
    right: -320px;
    height: 100%;
    z-index: 2001;

    @media (min-width: 320px) {
        right: -320px;
    }

    @media (min-width: 360px) {
        right: -360px;
    }

    @media (min-width: 375px) {
        right: -375px;
    }

    @media (min-width: 414px) {
        right: -414px;
    }

    @media (min-width: 500px) {
        right: -360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        top: 60px;
        left: 0;
        height: unset;
        max-width: 271px;
        padding: 0 0 1rem 0;
`;

//OTHER LEVELS
const SubLevelHolder = styled.ul<IMobileBreakpoint>`
    display: none;
    position: absolute;
    right: -320px;
    top: 0;
    padding: 0;
    background: #fff;
    list-style: none;

    @media (min-width: 320px) {
        right: -320px;
    }

    @media (min-width: 360px) {
        right: -360px;
    }

    @media (min-width: 375px) {
        right: -375px;
    }

    @media (min-width: 414px) {
        right: -414px;
    }

    @media (min-width: 500px) {
        right: -360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        right: -271px;
        left: unset;
        padding: 0 0 1rem 0;
        min-height: 100%;
    }
`;

interface ISubElement extends IMobileBreakpoint {
    level: 1 | 2 | 3;
}

const levelStyle = (props: ISubElement) => css`
    height: 50px;
    background: #fff;
    font-size: 1.24rem;
    &:hover {
        background: #fafaf5;
    }

    @media (min-width: ${props.mobileBreakpoint ? props.mobileBreakpoint : 1024}px) {
        background: ${(props.level === 1 && "#fff") || (props.level === 2 && "#f9f9f9") || (props.level === 3 && "#ededee")};
        font-size: ${(props.level === 1 && "1.244rem") || (props.level > 1 && "1.106rem")};
        height: ${(props.level === 1 && "6rem") || (props.level === 2 && "3rem") || (props.level === 3 && "3rem")};
    }
`;

const SubElement = styled.a`
    ${levelStyle};
    color: #23232d;
    padding: 2rem 2.5rem 2rem 4rem;
    background: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 400 !important;

    &:hover,
    &:focus {
        text-decoration: none;
        color: #23232d;
    }

    @media (min-width: 320px) {
        width: 320px;
    }

    @media (min-width: 360px) {
        width: 360px;
    }

    @media (min-width: 375px) {
        width: 375px;
    }

    @media (min-width: 414px) {
        width: 414px;
    }

    @media (min-width: 500px) {
        width: 360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        width: 271px;
        padding: 1.5rem 2rem;
    }
`;

const FirstLevelListElement = styled.li<IMobileBreakpoint>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        &:hover {
            .second-level {
                display: block;
                background: #f9f9f9;
            }
        }
    }
`;

const SecondLevelListElement = styled.li<ISecondLevelElementProps>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        height: 3rem;
        display: ${(props) => (props.hideOnDesktop ? "none" : "block")};
        &:hover {
            .third-level {
                display: block;
                height: 100%;
                background: #ededee;
            }
        }
    }
`;

const iconAndTitleHolder = css`
    display: flex;
    align-items: center;
`;

const iconHolder = css`
    margin-right: 2.6rem;
    display: flex;
    align-items: center;

    @media (min-width: 1311px) {
        margin-left: 0;
        margin-right: 0;
    }
`;
import React, {useEffect, useState} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {isEmpty} from "@pg-mono/nodash";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {NAV_MOBILE_BREAKPOINT_WITH_FAVOURITES} from "./constants/nav_mobile_breakpoint";
import {IMobileBreakpoint, INavLeftElement, NavGtmData, NavLevelType} from "./Navigation";
import {INavElementStyle, NavigationElementText} from "./NavigationElement";

interface IProps {
    dropdownElements: INavLeftElement[];
    navState: number[];
    setNavState: (update: (prev: number[]) => number[]) => void;
    title: string;
    url?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    itemIndex: number;
    arrow: React.JSX.Element;
    dropdownIndicator?: React.JSX.Element;
    setBlendVisible: (isVisible: boolean) => void;
    icon?: React.JSX.Element;
    isRightElement?: boolean;
    mobileBreakpoint?: number;
    hidingBreakpoint?: number;
    style?: INavElementStyle;
    elementTracking: (trackData?: NavGtmData) => void;
    amplitudeNavClickTracking?: (selectedTab: string, sectionNumber: number) => void;
}

export const DropdownListElement = (props: IProps) => {
    const [isMobile, setMobile] = useState(false);

    useEffect(() => {
        setMobile(window.innerWidth < (props.mobileBreakpoint ?? 1024));
    }, [props.mobileBreakpoint]);

    const onClick = (
        e: React.MouseEvent<HTMLElement>,
        level: NavLevelType,
        idx: number,
        subElementsLength: number,
        onClick: ((e: React.MouseEvent<HTMLElement>) => void) | undefined,
        selectedTab: string,
        sectionNumber: number
    ) => {
        props.elementTracking({level: level.toString(), text: e.currentTarget.innerText});
        props.amplitudeNavClickTracking?.(selectedTab, sectionNumber);
        if (isMobile && subElementsLength > 0) {
            if (props.navState.length < 3) {
                e.preventDefault();
            }
            props.setNavState((prev) => [...prev.slice(0, level - 1), idx]);
        }
        if (onClick) {
            onClick(e); // if you need to prevent default you should do it in callback function
            props.setBlendVisible(false);
        }
    };

    const mobileShowLevel = (level: NavLevelType, idx: number) => {
        if (isMobile && props.navState[level] === idx) {
            return {display: "block"};
        }
    };

    const onHoverToggle = (isActive: boolean) => {
        if (!isMobile) {
            props.setBlendVisible(isActive);
        }
    };

    return (
        <TopLevelListElement
            mobileBreakpoint={props.mobileBreakpoint}
            data-testid={NAVIGATION_TEST_IDS.MAIN_ELEMENT}
            onMouseEnter={() => onHoverToggle(true)}
            onMouseLeave={() => onHoverToggle(false)}
            onClick={(e) => {
                if (isMobile) {
                    props.onClick?.(e);
                }
            }}
        >
            <TopLevelListElementContent
                mobileBreakpoint={props.mobileBreakpoint}
                onClick={(e) => onClick(e, 1, props.itemIndex, props.dropdownElements.length, props.onClick, props.title, props.itemIndex)}
                href={props.url}
            >
                <div css={iconAndTitleHolder}>
                    {props.icon && <div css={iconHolder}>{props.icon}</div>}
                    <NavigationElementText elementStyle={props.style}>{props.title}</NavigationElementText>
                </div>

                <TopLevelArrow mobileBreakpoint={props.mobileBreakpoint}>{!isEmpty(props.dropdownElements) && props.arrow}</TopLevelArrow>

                <DesktopOnlyIndicator mobileBreakpoint={props.mobileBreakpoint}>
                    {!isEmpty(props.dropdownElements) && props.dropdownIndicator}
                </DesktopOnlyIndicator>
            </TopLevelListElementContent>

            <FirstLevelHolder
                data-testid="navigation-first-level"
                isRightElement={props.isRightElement}
                mobileBreakpoint={props.mobileBreakpoint}
                className="first-level"
                style={mobileShowLevel(0, props.itemIndex)}
            >
                {props.dropdownElements.map((firstLevelItem, firstLevelItemIndex) => (
                    <FirstLevelListElement key={firstLevelItemIndex} mobileBreakpoint={props.mobileBreakpoint} data-testid="navigation-first-level-element">
                        <SubElement
                            mobileBreakpoint={props.mobileBreakpoint}
                            onClick={(e) =>
                                onClick(
                                    e,
                                    2,
                                    firstLevelItemIndex,
                                    firstLevelItem.subElements ? firstLevelItem.subElements.length : 0,
                                    firstLevelItem.onClick,
                                    `${props.title}|${firstLevelItem.title}`,
                                    firstLevelItemIndex
                                )
                            }
                            level={1}
                            href={firstLevelItem.url}
                            style={firstLevelItem.style}
                            target={firstLevelItem.target}
                        >
                            {firstLevelItem.title}

                            <div>{!isEmpty(firstLevelItem.subElements) && props.arrow}</div>
                        </SubElement>

                        {!isEmpty(firstLevelItem.subElements) && (
                            <SubLevelHolder
                                mobileBreakpoint={props.mobileBreakpoint}
                                data-testid="navigation-second-level"
                                className="second-level"
                                style={mobileShowLevel(1, firstLevelItemIndex)}
                            >
                                {firstLevelItem.subElements?.map((secondaryLevelItem, secondaryLevelItemIndex) => (
                                    <SecondLevelListElement
                                        key={secondaryLevelItemIndex}
                                        mobileBreakpoint={props.mobileBreakpoint}
                                        data-testid="navigation-second-level-element"
                                        hideOnDesktop={secondaryLevelItem.hideOnDesktop}
                                    >
                                        <SubElement
                                            mobileBreakpoint={props.mobileBreakpoint}
                                            onClick={(e) =>
                                                onClick(
                                                    e,
                                                    3,
                                                    secondaryLevelItemIndex,
                                                    secondaryLevelItem.subElements ? secondaryLevelItem.subElements.length : 0,
                                                    secondaryLevelItem.onClick,
                                                    `${props.title}|${firstLevelItem.title}|${secondaryLevelItem.title}`,
                                                    secondaryLevelItemIndex
                                                )
                                            }
                                            level={2}
                                            href={secondaryLevelItem.url}
                                            target={secondaryLevelItem.target}
                                        >
                                            {secondaryLevelItem.title}

                                            <div>{!isEmpty(secondaryLevelItem.subElements) && props.arrow}</div>
                                        </SubElement>

                                        {!isEmpty(secondaryLevelItem.subElements) && (
                                            <SubLevelHolder
                                                mobileBreakpoint={props.mobileBreakpoint}
                                                data-testid="navigation-third-level"
                                                className="third-level"
                                                style={mobileShowLevel(2, secondaryLevelItemIndex)}
                                            >
                                                {secondaryLevelItem.subElements?.map((thirdLevelListElement, thirdLevelItemIndex) => {
                                                    return (
                                                        <li key={thirdLevelItemIndex} data-testid="navigation-third-level-element">
                                                            <SubElement
                                                                mobileBreakpoint={props.mobileBreakpoint}
                                                                onClick={(e) =>
                                                                    onClick(
                                                                        e,
                                                                        4,
                                                                        thirdLevelItemIndex,
                                                                        thirdLevelListElement.subElements ? thirdLevelListElement.subElements.length : 0,
                                                                        thirdLevelListElement.onClick,
                                                                        `${props.title}|${firstLevelItem.title}|${secondaryLevelItem.title}|${thirdLevelListElement.title}`,
                                                                        thirdLevelItemIndex
                                                                    )
                                                                }
                                                                level={3}
                                                                href={thirdLevelListElement.url}
                                                                target={thirdLevelListElement.target}
                                                            >
                                                                {thirdLevelListElement.title}
                                                            </SubElement>
                                                        </li>
                                                    );
                                                })}
                                            </SubLevelHolder>
                                        )}
                                    </SecondLevelListElement>
                                ))}
                            </SubLevelHolder>
                        )}
                    </FirstLevelListElement>
                ))}
            </FirstLevelHolder>
        </TopLevelListElement>
    );
};

export interface IFirstLevelHolderProps extends IMobileBreakpoint {
    isRightElement?: boolean;
}

interface ISecondLevelElementProps extends IMobileBreakpoint {
    hideOnDesktop?: boolean;
}

// TOP LEVEL
const TopLevelListElement = styled.li<IMobileBreakpoint>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        display: inline-block;
        width: unset;
        position: relative;

        &:hover {
            .first-level {
                display: block;
            }

            .dropdown-indicator svg {
                transform: rotate(180deg);
            }
        }
    }
`;

const TopLevelListElementContent = styled.a<IMobileBreakpoint>`
    height: 60px;
    padding: 2rem 2.5rem 2rem 4rem;
    font-size: 1.24rem;
    color: #23232d;
    cursor: pointer;
    width: 320px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:hover,
    &:focus {
        text-decoration: none;
        color: #23232d;
    }

    @media (min-width: 320px) {
        width: 320px;
    }

    @media (min-width: 360px) {
        width: 360px;
    }

    @media (min-width: 375px) {
        width: 375px;
    }

    @media (min-width: 414px) {
        width: 414px;
    }

    @media (min-width: 500px) {
        width: 360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        width: unset;
        padding: 2rem;
        white-space: nowrap;
    }

    ${(props) =>
        props.mobileBreakpoint && props.mobileBreakpoint >= NAV_MOBILE_BREAKPOINT_WITH_FAVOURITES
            ? ""
            : `
        @media (min-width: ${NAV_MOBILE_BREAKPOINT_WITH_FAVOURITES}px) {
            padding: 2rem 3rem;
        }
    `}
`;

const TopLevelArrow = styled.div<IMobileBreakpoint>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        display: none;
    }
`;

const DesktopOnlyIndicator = styled.span<IMobileBreakpoint>`
    display: none;

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        display: inline-flex;
    }
`;

// FIRST LEVEL
const FirstLevelHolder = styled.ul<IFirstLevelHolderProps>`
    display: none;
    position: absolute;
    background: #fff;
    list-style: none;
    padding: 0;
    margin: 0;
    top: 0;
    right: -320px;
    height: 100%;
    z-index: 2001;

    @media (min-width: 320px) {
        right: -320px;
    }

    @media (min-width: 360px) {
        right: -360px;
    }

    @media (min-width: 375px) {
        right: -375px;
    }

    @media (min-width: 414px) {
        right: -414px;
    }

    @media (min-width: 500px) {
        right: -360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint + 1 : 1024)}px) {
        top: 60px;
        left: 0;
        height: unset;
        max-width: 271px;
        padding: 0 0 1rem 0;
`;

//OTHER LEVELS
const SubLevelHolder = styled.ul<IMobileBreakpoint>`
    display: none;
    position: absolute;
    right: -320px;
    top: 0;
    padding: 0;
    background: #fff;
    list-style: none;

    @media (min-width: 320px) {
        right: -320px;
    }

    @media (min-width: 360px) {
        right: -360px;
    }

    @media (min-width: 375px) {
        right: -375px;
    }

    @media (min-width: 414px) {
        right: -414px;
    }

    @media (min-width: 500px) {
        right: -360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        right: -271px;
        left: unset;
        padding: 0 0 1rem 0;
        min-height: 100%;
    }
`;

interface ISubElement extends IMobileBreakpoint {
    level: 1 | 2 | 3;
}

const levelStyle = (props: ISubElement) => css`
    height: 50px;
    background: #fff;
    font-size: 1.24rem;
    &:hover {
        background: #fafaf5;
    }

    @media (min-width: ${props.mobileBreakpoint ? props.mobileBreakpoint : 1024}px) {
        background: ${(props.level === 1 && "#fff") || (props.level === 2 && "#f9f9f9") || (props.level === 3 && "#ededee")};
        font-size: ${(props.level === 1 && "1.244rem") || (props.level > 1 && "1.106rem")};
        height: ${(props.level === 1 && "6rem") || (props.level === 2 && "3rem") || (props.level === 3 && "3rem")};
    }
`;

const SubElement = styled.a`
    ${levelStyle};
    color: #23232d;
    padding: 2rem 2.5rem 2rem 4rem;
    background: #fff;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 400 !important;

    &:hover,
    &:focus {
        text-decoration: none;
        color: #23232d;
    }

    @media (min-width: 320px) {
        width: 320px;
    }

    @media (min-width: 360px) {
        width: 360px;
    }

    @media (min-width: 375px) {
        width: 375px;
    }

    @media (min-width: 414px) {
        width: 414px;
    }

    @media (min-width: 500px) {
        width: 360px;
    }

    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        width: 271px;
        padding: 1.5rem 2rem;
    }
`;

const FirstLevelListElement = styled.li<IMobileBreakpoint>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        &:hover {
            .second-level {
                display: block;
                background: #f9f9f9;
            }
        }
    }
`;

const SecondLevelListElement = styled.li<ISecondLevelElementProps>`
    @media (min-width: ${(props) => (props.mobileBreakpoint ? props.mobileBreakpoint : 1024)}px) {
        height: 3rem;
        display: ${(props) => (props.hideOnDesktop ? "none" : "block")};
        &:hover {
            .third-level {
                display: block;
                height: 100%;
                background: #ededee;
            }
        }
    }
`;

const iconAndTitleHolder = css`
    display: flex;
    align-items: center;
`;

const iconHolder = css`
    margin-right: 2.6rem;
    display: flex;
    align-items: center;

    @media (min-width: 1311px) {
        margin-left: 0;
        margin-right: 0;
    }
`;
